import UsersWithoutDocumentation from "../../../shared/components/cards/UsersWithoutDocumentation";
import CalibrationEquipmentDueExpire from "../../../shared/components/cards/CalibrationEquipmentDueExpire";
import ActionRequestPending from "../../../shared/components/cards/ActionRequestPending";
import PendingExportRecord from "../../../shared/components/cards/PendingExportRecord";
import Welcome from "./Welcome";
import React, { CSSProperties, useMemo } from "react";
import { useGetList, usePermissions } from "react-admin";
import { Instrument, Sacop, User } from "../../../shared/types/types";
import AnalysisOfSharesPending from "../../../shared/components/cards/AnalysisOfSharesPending";
import ImplementationOfActionPending from "../../../shared/components/cards/ImplementationOfActionPending";
import ClosingOfSharesPending from "../../../shared/components/cards/ClosingOfSharesPending";
import VerificationEffectivenessActionPending from "../../../shared/components/cards/VerificationEffectivenessActionPending";

const styles = {
    flex: { display: 'flex', margin: '0.5em' },
    flexColumn: { display: 'flex', flexDirection: 'column' },
    leftCol: { flex: 1, marginRight: '0.5em' },
    rightCol: { flex: 1, marginLeft: '0.5em' },
    singleCol: { marginTop: '1em', marginBottom: '1em' },
};

const Spacer = () => <span style={{ width: '1em' }} />;
const VerticalSpacer = () => <span style={{ height: '1em' }} />;

interface State {
    nbUsersWithoutDocumentation?: string;
    nbCalibrationEquipmentDueExpire?: string;
    nbActionRequestPending?: string;
    nbAnalysisOfShares?: string;
    nbImplementationOfActionPending?: string;
    nbClosingOfSharesPending?: string;
    nbVerificationEffectivenessActionPending?: string;
    nbPendingExportRecord?: string;
}

export const Dashboard = () => {

    const { isLoading, permissions } = usePermissions();

    const { data: users, total: totalUsersWithoutDocumentation } = useGetList<User>('user', {
        filter: { cvFile: null },
        sort: { field: 'username', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    const { data: instruments, total: totalCalibrationEquipmentDueExpire } = useGetList<Instrument>('instrument', {
        filter: { requireCalibration: true },
        sort: { field: 'name', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    // ✅ SOLICITUD DE ACCIÓN: Registros creados pero NO enviados a análisis
    const { data: requestForAction, total: totalActionRequestPending } = useGetList<Sacop>('requestForAction', {
        filter: { sendToResponsibleForAnalysis: false },
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    // ✅ ANÁLISIS DE ACCIONES: Enviado a análisis pero NO enviado a implementación
    const { data: analysisOfShares, total: totalAnalysisOfShares } = useGetList<Sacop>('analysisOfShares', {
        filter: {
            sendToResponsibleForAnalysis: true,
            sendToResponsibleForImplementation: false,
            dateOfFormationOfWorkTeam: null
        },
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    // ✅ IMPLEMENTACIÓN DE ACCIÓN: Enviado a implementación pero NO enviado a cierre
    const { data: implementationOfAction, total: totalImplementationOfAction } = useGetList<Sacop>('implementationOfAction', {
        filter: {
            sendToResponsibleForImplementation: true,
            sendToResponsibleForClosingOfActions: false,
            implementationDate: null
        },
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    // ✅ CIERRE DE ACCIONES: Enviado a cierre pero NO enviado a verificación
    const { data: closingOfShares, total: totalClosingOfShares } = useGetList<Sacop>('closingOfShares', {
        filter: {
            sendToResponsibleForClosingOfActions: true,
            sendToResponsibleForVerificationEffectivenessAction: false,
            closeDate: null
        },
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });

    // ✅ VERIFICACIÓN DE EFECTIVIDAD: Cierre completado pero sin verificación
    const { data: verificationEffectivenessAction, total: totalVerificationEffectivenessAction } = useGetList<Sacop>('verificationEffectivenessAction', {
        filter: {
            sendToResponsibleForVerificationEffectivenessAction: true,
            verificationDate: null
        },
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });


    const { data: pendingExportRecord, total: totalPendingExportRecord } = useGetList<Sacop>('weightReportOperation', {
        filter: {},
        sort: { field: 'id', order: 'DESC' },
        pagination: { page: 1, perPage: 1 },
    });



    const aggregation = useMemo<State>(() => {
        return {
            nbUsersWithoutDocumentation: totalUsersWithoutDocumentation !== undefined ? String(totalUsersWithoutDocumentation) : '-',
            nbCalibrationEquipmentDueExpire: totalCalibrationEquipmentDueExpire !== undefined ? String(totalCalibrationEquipmentDueExpire) : '-',
            nbActionRequestPending: totalActionRequestPending !== undefined ? String(totalActionRequestPending) : '-',
            nbAnalysisOfShares: totalAnalysisOfShares !== undefined ? String(totalAnalysisOfShares) : '-',
            nbPendingExportRecord: totalPendingExportRecord !== undefined ? String(totalPendingExportRecord) : '-',
            nbImplementationOfActionPending: totalImplementationOfAction !== undefined ? String(totalImplementationOfAction) : '-',
            nbClosingOfSharesPending: totalClosingOfShares !== undefined ? String(totalClosingOfShares) : '-',
            nbVerificationEffectivenessActionPending: totalVerificationEffectivenessAction !== undefined ? String(totalVerificationEffectivenessAction) : '-',
        };
    }, [totalUsersWithoutDocumentation
        , totalCalibrationEquipmentDueExpire
        , totalActionRequestPending
        , totalPendingExportRecord
        , totalAnalysisOfShares
        , totalImplementationOfAction
        , totalClosingOfShares
        , totalVerificationEffectivenessAction]);


    const { nbUsersWithoutDocumentation
            , nbCalibrationEquipmentDueExpire
            , nbActionRequestPending
            , nbAnalysisOfShares
            , nbPendingExportRecord
            , nbImplementationOfActionPending
            , nbClosingOfSharesPending
            , nbVerificationEffectivenessActionPending
        } = aggregation;

    return (isLoading
        ? <></>
        :
        <div>
            <div style={styles.flexColumn as CSSProperties}>
                <Welcome />

                {
                    (permissions.some(x => x === 'BACKOFFICE_USER_R') || permissions.some(x => x === 'BACKOFFICE_INSTRUMENT_R') ? (
                        <>
                            <div style={styles.flex}>
                                <div style={styles.leftCol}>
                                    <div style={styles.flex}>
                                        {
                                            (permissions.some(x => x === 'BACKOFFICE_USER_R') ? (
                                                <><UsersWithoutDocumentation value={nbUsersWithoutDocumentation} /><Spacer /></>
                                            ) : (
                                                <></>
                                            )
                                            )
                                        }
                                        {
                                            (permissions.some(x => x === 'BACKOFFICE_INSTRUMENT_R') ? (
                                                <> <CalibrationEquipmentDueExpire value={nbCalibrationEquipmentDueExpire} /></>
                                            ) : (
                                                <></>
                                            )
                                            )
                                        }
                                    </div>
                                </div>
                                <div style={styles.rightCol}>
                                    <div style={styles.flex}>
                                        <PendingExportRecord value={nbPendingExportRecord} />
                                        <Spacer />
                                        <ActionRequestPending value={nbActionRequestPending} />
                                    </div>
                                </div>
                            </div>
                            <div style={styles.flex}>
                                <div style={styles.leftCol}>
                                    <div style={styles.flex}>
                                        <AnalysisOfSharesPending value={nbAnalysisOfShares} />
                                        <Spacer />
                                        <ImplementationOfActionPending value={nbImplementationOfActionPending} />
                                    </div>
                                </div>
                                <div style={styles.rightCol}>
                                    <div style={styles.flex}>
                                        <ClosingOfSharesPending value={nbClosingOfSharesPending} />
                                        <Spacer />
                                        <VerificationEffectivenessActionPending value={nbVerificationEffectivenessActionPending} />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={styles.flex}>
                                <div style={styles.leftCol}>
                                    <div style={styles.flex}>
                                        <ActionRequestPending value={nbActionRequestPending} />
                                        <Spacer />
                                        <PendingExportRecord value={nbPendingExportRecord} />
                                    </div>
                                </div>
                            </div>
                        </>
                    ))
                }
            </div>
        </div>
    );
};

